// src/screens/EditPlayer.tsx
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { sanitizeFileName } from "../utils/storage";
import { ArrowLeft, Save, AlertTriangle, Camera, User2 } from "lucide-react";
import i18n from "../i18n";

type Player = {
  id: string;
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
  age?: number;
};

type TeamRow = { id: string; team_name: string; players: Player[] };

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const EditPlayer = () => {
  const { t } = useI18nSafe();

  const { playerId } = useParams(); // JSONB id du joueur
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<TeamRow | null>(null);
  const [player, setPlayer] = useState<Player | null>(null);

  // Champs éditables
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [number, setNumber] = useState<number | "">("");
  const [position, setPosition] = useState("");

  // Photo
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [currentSignedUrl, setCurrentSignedUrl] = useState<string | null>(null);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!playerId) return;

      setLoading(true);
      setErrorMsg(null);

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setErrorMsg(t("editplayer:errors.must_be_logged_in"));
        setLoading(false);
        return;
      }

      // Récupère toutes les équipes de l'utilisateur et retrouve celle qui contient ce joueur
      const { data: teams, error } = await supabase
        .from("teams")
        .select("id, team_name, players")
        .eq("user_id", user.id);

      if (error) {
        setErrorMsg(t("editplayer:errors.load_teams_failed"));
        setLoading(false);
        return;
      }

      let foundTeam: TeamRow | null = null;
      let foundPlayer: Player | null = null;

      for (const trow of (teams || []) as TeamRow[]) {
        const p = (trow.players || []).find((pl: any) => String(pl.id) === String(playerId));
        if (p) {
          foundTeam = trow;
          foundPlayer = p as Player;
          break;
        }
      }

      if (!foundTeam || !foundPlayer) {
        setErrorMsg(t("editplayer:errors.player_not_found"));
        setLoading(false);
        return;
      }

      setTeam(foundTeam);
      setPlayer(foundPlayer);
      setFirstName(foundPlayer.firstName || "");
      setLastName(foundPlayer.lastName || "");
      setNumber(foundPlayer.number ?? "");
      setPosition(foundPlayer.position || "");

      if (foundPlayer.photoUrl) {
        const { data: signed } = await supabase.storage
          .from("player-photos")
          .createSignedUrl(foundPlayer.photoUrl, 3600);
        setCurrentSignedUrl(signed?.signedUrl ?? null);
      } else {
        setCurrentSignedUrl(null);
      }

      setLoading(false);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const onPhotoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setPhotoFile(file);
    if (file) {
      setPhotoPreview(URL.createObjectURL(file));
    } else {
      setPhotoPreview(null);
    }
  };

  const onSave = async () => {
    if (!team || !player) return;

    // Validation simple
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg(t("editplayer:errors.firstname_lastname_required"));
      return;
    }
    if (number === "" || isNaN(Number(number))) {
      setErrorMsg(t("editplayer:errors.invalid_number"));
      return;
    }

    // Optionnel : prévenir si le numéro est déjà utilisé dans l'équipe
    const num = Number(number);
    const conflict = (team.players || []).some(
      (p: any) => String(p.id) !== String(player.id) && Number(p.number) === num
    );
    if (conflict) {
      const proceed = window.confirm(t("editplayer:confirm.number_conflict"));
      if (!proceed) return;
    }

    setSaving(true);
    setErrorMsg(null);

    // Upload éventuel de la nouvelle photo
    let photoUrl = player.photoUrl;
    if (photoFile) {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) {
        setSaving(false);
        setErrorMsg(t("editplayer:errors.must_be_logged_in"));
        return;
      }

      const fileName = `${Date.now()}-${sanitizeFileName(photoFile.name)}`;
      const filePath = `players/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("player-photos")
        .upload(filePath, photoFile, { cacheControl: "3600", upsert: true });

      if (uploadError) {
        setSaving(false);
        setErrorMsg(t("editplayer:errors.photo_upload_failed"));
        return;
      }

      photoUrl = filePath;
    }

    const updatedPlayers = (team.players || []).map((p: any) =>
      String(p.id) === String(player.id)
        ? { ...p, firstName: firstName.trim(), lastName: lastName.trim(), number: num, position: position.trim(), photoUrl }
        : p
    );

    const { error } = await supabase
      .from("teams")
      .update({ players: updatedPlayers })
      .eq("id", team.id);

    setSaving(false);

    if (error) {
      setErrorMsg(t("editplayer:errors.save_failed"));
      return;
    }

    // Retour à la fiche joueur (by-id)
    navigate(`/player/by-id/${player.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 grid place-items-center">
        <p className="text-gray-600 dark:text-gray-400">{t("common:loading")}</p>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 p-6 max-w-xl mx-auto">
        <Link
          to={player ? `/player/by-id/${player.id}` : "/myteam"}
          className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common:back")}
        </Link>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-start gap-3 text-red-600 dark:text-red-400">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <p>{errorMsg}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!team || !player) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <div className="p-6 max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common:back")}
          </button>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm">
          <h1 className="text-xl font-semibold mb-4">
            {t("editplayer:title", { team: team.team_name })}
          </h1>

          <div className="flex flex-col items-center mb-6">
            <div className="relative">
              {photoPreview || currentSignedUrl ? (
                <img
                  src={photoPreview || currentSignedUrl || undefined}
                  alt={`${firstName} ${lastName}`}
                  className="w-24 h-24 rounded-full object-cover ring-2 ring-orange-500/20"
                />
              ) : (
                <div className="w-24 h-24 rounded-full grid place-items-center bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <User2 className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-600 hover:bg-orange-700 text-white shadow"
                title={t("editplayer:actions.change_photo")}
              >
                <Camera className="w-4 h-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onPhotoSelected}
                className="hidden"
              />
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 text-sm text-orange-600 dark:text-orange-400 hover:underline"
            >
              {t("editplayer:actions.change_photo")}
            </button>
          </div>

          <div className="grid gap-4">
            <div className="grid gap-1">
              <label className="text-sm text-gray-600 dark:text-gray-400">{t("editplayer:fields.firstname")}</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
              />
            </div>

            <div className="grid gap-1">
              <label className="text-sm text-gray-600 dark:text-gray-400">{t("editplayer:fields.lastname")}</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
              />
            </div>

            <div className="grid gap-1">
              <label className="text-sm text-gray-600 dark:text-gray-400">{t("editplayer:fields.number")}</label>
              <input
                type="number"
                value={number}
                onChange={(e) => setNumber(e.target.value === "" ? "" : Number(e.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
              />
            </div>

            <div className="grid gap-1">
              <label className="text-sm text-gray-600 dark:text-gray-400">{t("editplayer:fields.position")}</label>
              <input
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder={t("editplayer:placeholders.position")}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
              />
            </div>
          </div>

          {errorMsg && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400">{errorMsg}</p>
          )}

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg"
            >
              <Save className="w-4 h-4" />
              {t("editplayer:actions.save")}
            </button>
            <button
              onClick={() => navigate(`/player/by-id/${player.id}`)}
              className="text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
            >
              {t("common:cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
